import { electronRendererHostAdapter } from "./host-electron";
import type { RendererHostCapabilities } from "./host-capabilities";

export type RendererDiagnosticLogPayload = {
  level?: "info" | "warn" | "error";
  scope?: string;
  event: string;
  message?: string;
  details?: unknown;
};

export type LocalPathMenuAnchor = {
  x: number;
  y: number;
};

export type DesktopUpdaterStatus = {
  ready: boolean;
  version: string | null;
  state: DesktopUpdateState;
  entryVisible: boolean;
  attention: boolean;
  installReady: boolean;
  retryReady: boolean;
  label: "Update" | "Continue" | "Updated" | null;
  targetVersion: string | null;
};

export type BustlyWorkspaceIntegrationsOptions = {
  /** Opens the read-only Admin detail surface for this app. */
  integration?: string | null;
  /** Starts an explicit Admin connection flow. */
  platform?: string | null;
  intent?: "connect" | "detail";
};

export type BustlyWorkspaceRecordPage = "value-ledger" | "workspace-skills";

export type DesktopUpdateStatusPayload = {
  event: string;
  source?: "startup" | "focus" | "menu" | "renderer";
  forceReveal?: boolean;
  state?: DesktopUpdateState;
  status?: DesktopUpdaterStatus;
};

export type RendererHostAdapter = {
  readonly platform: "electron" | "cloud" | "custom";
  gatewayStatus: () => Promise<GatewayStatus>;
  gatewayStart: () => Promise<{ success: boolean; error?: string }>;
  gatewayConnectConfig: () => Promise<GatewayConnectConfig>;
  gatewayRestoreLastGoodConfig: () => Promise<{
    success: boolean;
    error?: string;
  }>;
  openclawInit: (
    options?: PresetConfigOptions,
  ) => Promise<InitializationResult>;
  openclawIsInitialized: () => Promise<boolean>;
  getAppInfo: () => Promise<AppInfo>;
  getHostCapabilities: () => Promise<RendererHostCapabilities>;
  bustlyIsLoggedIn: () => Promise<{
    success: boolean;
    loggedIn: boolean;
    error?: string;
  }>;
  bustlyLogin: () => Promise<{
    success: boolean;
    loginTraceId?: string;
    error?: string;
  }>;
  bustlyPollLogin: (
    loginTraceId: string,
  ) => Promise<{ success: boolean; pending: boolean; error?: string }>;
  bustlyCancelLogin: (
    loginTraceId?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyGetUserInfo: () => Promise<{
    success: boolean;
    user: BustlyUserInfo | null;
    error?: string;
  }>;
  bustlyLogout: () => Promise<{ success: boolean; error?: string }>;
  bustlyOpenSettings?: (
    workspaceId?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceSettings?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceIntegrations?: (
    workspaceId: string,
    options?: BustlyWorkspaceIntegrationsOptions,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceInvite?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceManage?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspacePricing?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenBusinessGroups?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenMarketIntelligence?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceAuditLog?: (
    workspaceId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceRecord?: (
    workspaceId: string,
    page: BustlyWorkspaceRecordPage,
  ) => Promise<{ success: boolean; error?: string }>;
  bustlyOpenWorkspaceCreate?: (
    workspaceId?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  resolvePastedPath?: (params: {
    file?: File;
    entryPath?: string;
    entryName?: string;
    transferPaths?: string[];
    fallbackKind: "file" | "directory";
  }) => Promise<{ path: string; kind: "file" | "directory" | null }>;
  selectChatContextPaths?: () => Promise<ChatContextPathSelection[]>;
  resolveChatImagePreview?: (path: string) => Promise<string | null>;
  resolveChatMediaPreview?: (path: string) => Promise<{
    dataUrl: string;
    mimeType: string;
    kind: "image" | "video" | "audio";
  } | null>;
  showChatImageContextMenu?: (params: {
    srcUrl?: string;
    localPath?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  resolveLocalPathHref?: (path: string) => Promise<{
    href: string;
    target?: "_blank" | "_self";
    rel?: string;
  } | null>;
  shouldLinkifyMarkdownLocalPaths?: () => boolean;
  openLocalPath?: (path: string) => Promise<{ success: boolean; error?: string }>;
  showLocalPathOpenWithMenu?: (
    path: string,
    anchor: LocalPathMenuAnchor,
  ) => Promise<{ success: boolean; error?: string }>;
  revealLocalPath?: (path: string) => Promise<{ success: boolean; error?: string }>;
  openExternalUrl?: (url: string) => Promise<{ success: boolean; error?: string }>;
  getNativeFullscreenStatus?: () => Promise<{ isNativeFullscreen: boolean }>;
  updaterCheck?: () => Promise<{ success: boolean; error?: string }>;
  updaterStartInstall?: () => Promise<{ success: boolean; error?: string }>;
  updaterInstall?: () => Promise<{ success: boolean; error?: string }>;
  updaterStatus?: () => Promise<DesktopUpdaterStatus>;
  consumePendingDeepLink?: () => Promise<DeepLinkData | null>;
  logRendererDiagnostic?: (
    payload: RendererDiagnosticLogPayload,
  ) => Promise<void>;
  onGatewayLifecycle?: (
    callback: (data: GatewayLifecycleData) => void,
  ) => () => void;
  onBustlyLoginRefresh?: (callback: () => void) => () => void;
  onBustlyLoginProgress?: (callback: () => void) => () => void;
  onNativeFullscreenChange?: (
    callback: (data: { isNativeFullscreen: boolean }) => void,
  ) => () => void;
  onUpdateStatus?: (
    callback: (data: DesktopUpdateStatusPayload) => void,
  ) => () => void;
  onDeepLink?: (callback: (data: DeepLinkData) => void) => () => void;
};

declare global {
  interface Window {
    __OPENCLAW_RENDERER_HOST_ADAPTER__?: RendererHostAdapter;
  }
}

let hostAdapterOverride: RendererHostAdapter | null = null;

export function setRendererHostAdapter(
  adapter: RendererHostAdapter | null,
): void {
  hostAdapterOverride = adapter;
}

export function getRendererHostAdapter(): RendererHostAdapter {
  if (hostAdapterOverride) {
    return hostAdapterOverride;
  }
  if (window.__OPENCLAW_RENDERER_HOST_ADAPTER__) {
    return window.__OPENCLAW_RENDERER_HOST_ADAPTER__;
  }
  if (window.electronAPI) {
    return electronRendererHostAdapter;
  }
  throw new Error("No renderer host adapter configured");
}
